import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { mdiDownload } from '@mdi/js';

import { useGroupUsersList } from 'user/hooks/group/useGroupUsersList';
import {
    IContentTypeValue,
    IGroupFragment,
    IUserSelectorRole,
    IGroupUsersQueryVariables,
    IFeature,
    RemoveUsersFromGroupDocument,
    ExportGroupParticipantsDocument,
    CurrentUserDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { useGroupUserCount } from 'user/hooks/group/useGroupUserCount';
import { useUserTableData } from 'user/hooks/useUserTableData';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useFeature } from 'common/hooks/useFeature';
import { UserSelector } from 'user/components/UserSelector';
import { Button } from 'common/components/Button';
import { Icon } from 'common/components/Icon';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { parseJSON } from 'common/utils/json';

import { AddUsersButton } from './AddUsersButton';

interface IProps {
    group: IGroupFragment;
    onUserUpdate?(): void;
}

export const UsersView = ({ group, onUserUpdate }: IProps) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();
    const client = useApolloClient();
    const [displaySnackbar] = useSnackbar();
    const { canUse: canExport } = useFeature(IFeature.Export);
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [exportReport, { loading: loadingExport }] = useMutation(
        ExportGroupParticipantsDocument
    );
    const { data: currentUserData } = useQuery(CurrentUserDocument);
    const currentUserEmail = currentUserData?.currentUser?.email;

    const { id: groupId, permissions, isEditable } = group;
    const { updateGroupUserCount } = useGroupUserCount({ groupId });

    const {
        loading: loadingUsers,
        users,
        tableDataProps,
    } = useUserTableData<IGroupUsersQueryVariables>(
        useGroupUsersList,
        undefined,
        { id: groupId }
    );
    const [removeUsersFromGroup, { loading: loadingRemoveUsers }] = useMutation(
        RemoveUsersFromGroupDocument
    );

    const userAmount = users?.length || 0;

    const handleRemoveParticipantsFromGroup = async (
        selectedUsers: string[]
    ) => {
        try {
            const result = await removeUsersFromGroup({
                variables: {
                    groupId,
                    userIds: selectedUsers,
                },
            });

            const { removedIds, failedIds } =
                result.data?.removeUsersFromGroup || {};

            if (removedIds?.length) {
                displaySnackbar(
                    translate('groupForm.participantsRemovedSuccess', {
                        count: removedIds.length,
                    }),
                    { variant: 'success' }
                );

                updateGroupUserCount(-removedIds.length);

                // Refetch the selector and the participants list
                client.cache.evict({ fieldName: 'paginatedUsers' });
                client.cache.gc();
                client.refetchQueries({ include: ['GroupUsers'] });

                onUserUpdate?.();
            }

            if (failedIds?.length) {
                displaySnackbar(
                    translate('groupForm.participantsRemovedFailed', {
                        count: failedIds.length,
                    }),
                    { variant: 'error' }
                );
            }
        } catch (error) {
            showApolloError(error);
        }
    };

    const handleExport = async () => {
        try {
            const exportResults = await exportReport({
                variables: { groupId },
            });
            const fileUrl =
                exportResults.data?.exportGroupParticipants?.fileUrl;

            if (fileUrl) {
                window.open(fileUrl, '_blank');
            } else {
                displaySnackbar(translate('groupForm.userExportSuccess'), {
                    variant: 'success',
                });
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        setShowExportDialog(false);
    };

    const permissionsObj = parseJSON(permissions ?? '') || {};
    const canAddParticipants = !!permissionsObj?.canAddParticipants;

    const exportButton = canAddParticipants && canExport && (
        <Button
            disabled={!users?.length}
            startIcon={<Icon path={mdiDownload} size="1.9rem" />}
            sx={{ flex: 1 }}
            variant="outlined"
            onClick={() => setShowExportDialog(true)}
        />
    );

    const selectorProps = {
        addedUsers: users,
        filterBarAppend: exportButton,
        loading: loadingUsers,
        noResultsLabel: translate('noOptionsText.participants'),
        tableDataProps,
    };

    return (
        <>
            {isEditable ? (
                <AddUsersButton
                    group={group}
                    userAmount={userAmount}
                    onUserUpdate={onUserUpdate}
                >
                    {({
                        onAddUsers,
                        loadingAddUsers,
                        isSelectorOpen,
                        onSelectorOpenChange,
                    }) => (
                        <UserSelector
                            {...selectorProps}
                            deleteDialogProps={{
                                title: translate(
                                    'groupForm.userDeleteDialog.title'
                                ),
                                text: translate(
                                    'groupForm.userDeleteDialog.text'
                                ),
                            }}
                            drawerOpen={isSelectorOpen}
                            label={translate('groupForm.addParticipants')}
                            loading={loadingUsers || loadingAddUsers}
                            loadingRemove={loadingRemoveUsers}
                            selectorQueryVariables={{
                                contentType: IContentTypeValue.Group,
                                objectId: group.id || groupId,
                                objectRoles: [IUserSelectorRole.Participant],
                            }}
                            showAddButton={false}
                            onAddUsers={onAddUsers}
                            onDrawerOpenChange={onSelectorOpenChange}
                            onRemoveUsers={handleRemoveParticipantsFromGroup}
                        />
                    )}
                </AddUsersButton>
            ) : (
                <UserSelector
                    {...selectorProps}
                    isEditable={false}
                    onAddUsers={() => {}}
                    onRemoveUsers={() => {}}
                />
            )}

            <ConfirmDialog
                confirmText={translate('sendExport')}
                loading={loadingExport}
                open={showExportDialog}
                title={translate(`groupForm.userExportDialog.title`)}
                onCancel={() => setShowExportDialog(false)}
                onClose={() => setShowExportDialog(false)}
                onConfirm={handleExport}
            >
                {translate(`groupForm.userExportDialog.text`, {
                    email: currentUserEmail,
                })}
            </ConfirmDialog>
        </>
    );
};
